import React, { useEffect, useState, useRef } from 'react';

const CardKanji = ({ kanji, heisig_keyword, on_readings, kun_readings, showOverride, resetSignal, onTogglePending, isPending }) => {

  const localKey = `kanji-${kanji}`;

  const isDisabled = kanji === '';
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const tapTimeoutRef = useRef(null);
  const lastTapTimeRef = useRef(0);
  const longPressTimeoutRef = useRef(null);
  const longPressedRef = useRef(false);

  const [showDetails, setShowDetails] = useState(() => {
    const saved = localStorage.getItem(localKey);
    return saved === 'true';
  });

  // Load saved value only if not overridden
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof showOverride === 'undefined') {
      const saved = localStorage.getItem(localKey);
      setShowDetails(saved === 'true');
    }
  }, [resetSignal, showOverride, localKey]);

  useEffect(() => {
    const saved = localStorage.getItem(localKey);
    if (saved !== null) {
      setShowDetails(saved === 'true');
    }
  }, [localKey]);

  // Apply override
  useEffect(() => {
    if (typeof showOverride === 'boolean') {
      setShowDetails(showOverride);
    }
  }, [showOverride]);

  useEffect(() => {
    localStorage.setItem(localKey, showDetails.toString());
  }, [showDetails, localKey]);

  // Load voices on mount
  useEffect(() => {
    const loadVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();
      setVoices(allVoices);

      // Pick a default voice (English US)
      const defaultVoice = allVoices.find(voice => voice.lang === 'ja-JP');//'en-GB'
      setSelectedVoice(defaultVoice || allVoices[0]);
    };

    // Some browsers need this event
    window.speechSynthesis.onvoiceschanged = loadVoices;

    // Try loading immediately too
    loadVoices();
  }, []);

  const handleTap = (e) => {
    const now = Date.now();
    const isTouch = e.type.startsWith('touch');
    const eventPhase = isTouch
      ? (e.type === 'touchstart' ? 'start' : 'end')
      : (e.type === 'mousedown' ? 'start' : 'end');

    clearTimeout(longPressTimeoutRef.current);

    if (eventPhase === 'start') {
      longPressTimeoutRef.current = setTimeout(() => {
        onTogglePending(kanji);
        longPressedRef.current = true;
      }, 500); // Long press threshold
    }

    if (eventPhase === 'end') {
      if (longPressedRef.current) {
        longPressedRef.current = false;
        return;
      }

      const timeSinceLastTap = now - lastTapTimeRef.current;
      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        // Double tap
        clearTimeout(tapTimeoutRef.current);
        setShowDetails((prev) => !prev);
      } else {
        // Single tap
        tapTimeoutRef.current = setTimeout(() => {
          const utterance = new SpeechSynthesisUtterance(kanji);
          utterance.lang = 'ja-JP';
          utterance.rate = 0.1;
          utterance.pitch = 1;
          utterance.volume = 1;
          if (selectedVoice) utterance.voice = selectedVoice;
          window.speechSynthesis.speak(utterance);
        }, 300);
      }
      lastTapTimeRef.current = now;
    }

    if (isTouch) e.preventDefault(); // Prevent mobile double-tap zoom
  };

  return (
    <div
      role="button"
      onTouchStart={handleTap}
      onTouchEnd={handleTap}
      onMouseDown={handleTap}
      onMouseUp={handleTap}
      tabIndex={isDisabled ? -1 : 0}  // Disable focus if kanji is empty
      className={`bg-white p-4 shadow rounded border border-gray-200 
        ${isDisabled ? 'pointer-events-none cursor-not-allowed' : 'cursor-pointer hover:shadow-lg active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500'} 
        ${isPending ? 'bg-yellow-300' : 'bg-white'}
        transition transform`}
    >
      <h3 className="text-lg font-semibold">{kanji}</h3>
      {showDetails && (
        <div>
          <p className="text-gray-500">{heisig_keyword}</p>
          <p className="text-gray-300">{on_readings}</p>
          <p className="text-gray-300">{kun_readings}</p>
        </div>
      )}
    </div>
  );
};

export default CardKanji;